import deleteCompanyApi from '@/api/delete/company/delete-company';
import getCompaniesApi from '@/api/get/company/get-companies';
import Alert from '@/components/alert/alert-component';
import { PrimaryButton } from '@/components/button/button-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import PageWrapper from '@/components/page-wrapper/page-wrapper-component';
import Pill from '@/components/pill/pill-component';
import StatusIndicator from '@/components/status-indicator/status-indicator-component';
import TableRowLogo from '@/components/table/components/logo/table-row-logo.component';
import ViewEditDelete from '@/components/table/components/view-edit-delete/view-edit-delete';
import Table from '@/components/table/table';
import { useLazyQuery, useMutation } from '@/hooks/use-api';
import { Heading2Text } from '@/utils/global-fonts';
import { useCallback, useEffect, useState } from 'react';
import { FiEdit } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import Styled from './companies-all-style';

const noWhiteSpace = { whiteSpace: 'nowrap' };

/**
 * Page that shows all Companies in a table format.
 * @returns {JSX.Element} The rendered Company Management page component.
 */
const CompaniesAll = () => {
  const tableId = 'company-list';
  const [fetchingPresets, setFetchingPresets] = useState(true);
  const [defaultVariables, setDefaultVariables] = useState({});
  const [defaultSort, setDefaultSort] = useState('created_at:DESC');
  const navigate = useNavigate();
  const {
    data,
    count,
    error,
    loading: isLoading,
    refetch,
  } = useLazyQuery(getCompaniesApi);

  const getTablePresets = useCallback(() => {
    const tablePreferences = localStorage.getItem('tablePreferences');
    if (tablePreferences) {
      const tableConfigs = JSON.parse(tablePreferences)[tableId];

      if (tableConfigs) {
        const { query } = tableConfigs;
        if (query?.length > 0) {
          // Get cached search value and apply it
          const searchQuery = query?.filter((q) => q.includes('search='));
          if (searchQuery.length > 0) {
            const tempSearch = searchQuery[0].replace('search=', '');
            setDefaultVariables({ ...defaultVariables, search: tempSearch });
          }

          // Get cached sort value and apply it
          const sortQuery = query?.filter((q) => q.includes('sort='));
          if (sortQuery.length > 0) {
            const tempSort = sortQuery[0].replace('sort=', '');
            setDefaultSort(tempSort);
          }

          // Get cached page size and apply it
          const pageSizeQuery = query?.filter((q) => q.includes('limit='));
          if (pageSizeQuery.length > 0) {
            const tempPageSize = parseInt(
              pageSizeQuery[0].replace('limit=', ''),
              10,
            );
            setDefaultVariables({ ...defaultVariables, limit: tempPageSize });
          }

          // Get cached filter values and apply it
          const filterQuery = query?.filter((q) => q.includes('filter='));

          if (filterQuery?.length > 0) {
            filterQuery?.forEach((f) => {
              const tempFilter = f.replace('filter=', '');
              if (tempFilter.includes('$') && tempFilter.includes('=')) {
                const filterKey = tempFilter.split('$')[0];
                const filterValue = tempFilter.split('=')[1];
                setDefaultVariables((prev) => ({
                  ...prev,
                  filter: {
                    ...prev.filter,
                    [filterKey]: { value: filterValue },
                  },
                }));
              }
            });
          }
        }
      }
    }
    setFetchingPresets(false);

    // This is disabled because we don't want to re-run this function when the defaultVariables changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // USE EFFECTS
  useEffect(() => {
    getTablePresets();
  }, [getTablePresets]);

  const [deleteCompany, { loading: deleteCompanyLoading }] =
    useMutation(deleteCompanyApi);

  const launchDeleteConfirmation = useCallback(
    (companyData) => {
      ConfirmationModal({
        title: 'Delete Company',
        text: `Are you sure you want to delete ${companyData.values.name}?`,
        onConfirm: () =>
          deleteCompany(companyData?.original?.id, companyData).then(() => {
            refetch().then(() => {
              Alert({
                icon: 'success',
                text: 'Company deleted successfully',
              });
            });
          }),
      });
    },
    [deleteCompany, refetch],
  );

  const renderLogoAndUniqueId = ({ row }) =>
    row?.original?.logo && <TableRowLogo src={row.original.logo} />;

  const renderStatusPill = ({ row }) => (
    <StatusIndicator status={row.original.status.toLowerCase()} />
  );

  const renderPill = ({ row }) => {
    const isSupplier = row.original.is_supplier;
    const isDealer = row.original.is_dealer;
    return (
      <>
        {isDealer && <Pill type="dealer" />}
        {isSupplier && <Pill type="supplier" />}
      </>
    );
  };

  const handleView = useCallback(
    (row) => {
      const companyDetails = row.original;
      navigate(`view/${companyDetails.id}`);
    },
    [navigate],
  );

  const handleEdit = useCallback(
    (row) => {
      const companyDetails = row.original;
      navigate(`edit/${companyDetails.id}`);
    },
    [navigate],
  );

  const Actions = useCallback(
    ({ row }) => (
      <ViewEditDelete
        feature="company"
        onEdit={() => handleEdit(row)}
        onDelete={() => launchDeleteConfirmation(row)}
        // TODO Add a manage company text to the view button if the company is a supplier
        onView={() => handleView(row)}
        viewText="Manage Company"
        onDuplicate={null}
      />
    ),
    [handleEdit, handleView, launchDeleteConfirmation],
  );

  const filters = [
    {
      header: 'Permission:',
      accessor: 'is_supplier',
      placeholder: 'Please select a Permission',
      type: 'select',
      options: [
        { value: false, text: 'Dealer' },
        { value: true, text: 'Supplier' },
      ],
    },
  ];

  const CompanyColumns = [
    {
      Header: '',
      accessor: 'id',
      search: true,
      id: 'id',
      width: '40px',
      Cell: renderLogoAndUniqueId,
      inputSort: true,
    },
    {
      Header: 'COMPANY NAME',
      id: 'name',
      accessor: 'name',
      search: true,
    },
    {
      Header: 'REGISTRATION  NUMBER',
      id: 'registration_number',
      accessor: 'registration_number',
      search: true,
    },
    {
      Header: 'EMAIL ADDRESS',
      id: 'email',
      accessor: 'email',
      search: true,
    },
    {
      Header: 'CONTACT NUMBER',
      id: 'contact_number',
      accessor: 'contact_number',
      search: true,
    },
    {
      Header: 'PERMISSION TYPE',
      id: 'is_supplier',
      accessor: 'is_supplier',
      search: true,
      Cell: (props) => renderPill(props),
    },
    {
      Header: 'STATUS',
      id: 'status',
      accessor: 'status',
      search: true,
      Cell: (props) => renderStatusPill(props),
    },
    {
      Header: 'ACTIONS',
      id: 'actions',
      Cell: (props) => Actions(props),
    },
  ];

  return (
    <PageWrapper>
      <Styled.MainPageContainer>
        <Styled.Heading>
          <Heading2Text style={noWhiteSpace}>Company Management</Heading2Text>
          <PermissionWrapper permissions={['company.create']}>
            <PrimaryButton
              title="New Company"
              onClick={() => navigate('new')}
              buttonHeight="40px"
              icon={<FiEdit />}
            />
          </PermissionWrapper>
        </Styled.Heading>
        <Table
          columns={CompanyColumns}
          data={data ?? []}
          tableOptions={['search', 'filter', 'columns']}
          id={tableId}
          defaultSort={defaultSort}
          defaultVariables={defaultVariables}
          dataCount={count ?? 0}
          downloadAccessor="company-list"
          error={error}
          loading={deleteCompanyLoading || isLoading || fetchingPresets}
          filters={filters}
          refetch={refetch}
          getDownloadData={getCompaniesApi}
          feature="company"
          onRowEdit={(row) => handleEdit(row)}
          onRowDelete={(row) => launchDeleteConfirmation(row)}
          onRowView={(row) => handleView(row)}
          viewText="Manage Company"
        />
      </Styled.MainPageContainer>
    </PageWrapper>
  );
};

export default CompaniesAll;
